// ============================================================================
// Every service line pays its own subcontractor — the words the project page
// and the project list use to say how far that has got.
// Item #680, TCSD-0380 (2026-10-02); first written 2026-10-01 for the Sub
// payment box, which this replaces.
//
// ⛔ ONE SOURCE OF THE WORDS. The For finance panel's "Lines paid" row and the
// project list's "Sub pay" chip both come from here; neither screen writes the
// sentence itself.
//
// ⛔ "PAID" MEANS A PAYMENT RECORD COVERS IT — the line's own, or one for the
// whole project. Jose's AP report is the record (Decision 1, 2026-10-02): its
// rows are linked to the line or project they paid, so a linked AP row reads as
// paid exactly like a "Pay this line" record does.
//
// Nothing here copies a line's sub up to the project. The lines own it.
// ============================================================================
import { canReadSubPaymentRecords } from '../../../shared/role-gates';
import type { ProjectLinePayment } from '../hooks/use-project-data';
import { fmtCurrency } from './fmt-currency';

export interface LinePaySummary {
  /** Service lines with an amount above zero — the only lines there is anything to pay on. */
  total: number;
  paid: number;
  /** What is still to pay on the unpaid lines, before any deduction. */
  unpaidAmount: number;
  /** Chargeback lines (negative amounts): money taken back from a sub. */
  deductions: number;
  /** A payment for the whole project, which stops line payments (guard A). */
  projectPaymentId: string | null;
  /** The For finance panel's "Lines paid" value, or null when there is nothing to say. */
  linesPaid: string | null;
}

/**
 * Can this reader see payment records at all? Mirrors the partner_payments read
 * policies — finance, executive and superuser, by primary role. For anyone else
 * an empty payment list means "not allowed to look", and the words below must
 * not turn that into "not paid". One copy of this rule, for every screen here.
 */
export function canReadPaymentRecords(role: string | null | undefined): boolean {
  return canReadSubPaymentRecords(role);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * The words, from the rows of v_project_line_payment_preview plus the whole-project
 * payment the page already reads (a project with no line has no preview rows).
 */
export function summariseLinePayments(
  rows: ProjectLinePayment[],
  wholeProjectPaymentId: string | null = null,
  wholeProjectAmount: number | null = null,
): LinePaySummary {
  const payable = rows.filter((r) => (r.amount ?? 0) > 0);
  const total = payable.length;
  const unpaidRows = payable.filter((r) => !r.existing_payment_id);
  const paid = total - unpaidRows.length;
  const unpaidAmount = unpaidRows.reduce((a, r) => a + Number(r.amount ?? 0), 0);
  const deductions = rows.filter((r) => (r.amount ?? 0) < 0).length;
  const projectPaymentId =
    wholeProjectPaymentId ?? rows.find((r) => !!r.project_payment_id)?.project_payment_id ?? null;
  const dWords = deductions > 0 ? ` · ${deductions} ${plural(deductions, 'deduction', 'deductions')}` : '';

  let linesPaid: string | null;
  if (projectPaymentId) {
    // A note with no line whose AP row is a deduction (money taken back from a
    // sub, e.g. the floor machine on PRJ-2026-09-0008) was not "paid".
    linesPaid =
      total > 0
        ? `${total} of ${total} (paid as one project)`
        : (wholeProjectAmount ?? 0) < 0
          ? 'Deduction recorded'
          : 'Paid as one project';
  } else if (total === 0) {
    linesPaid = deductions > 0 ? `No line to pay${dWords}` : null;
  } else if (paid === total) {
    linesPaid = `Paid per line${dWords}`;
  } else {
    linesPaid = `${paid} of ${total} · ${fmtCurrency(unpaidAmount)} to pay${dWords}`;
  }

  return { total, paid, unpaidAmount, deductions, projectPaymentId, linesPaid };
}

/** The shape the project list already loads: its lines, and the payment records on them. */
export interface ListRowForSubPay {
  completion_date: string | null;
  stage: string | null;
  source?: string | null;
  project_services?:
    | {
        line_role: string | null;
        sub_rate: number | null;
        assigned_partner_id: string | null;
        partner_payments?: { payment_id: string }[] | null;
      }[]
    | null;
  partner_payments?: { payment_id: string }[] | null;
}

/**
 * The project list's "Sub pay" chip, or null for no chip. The same rule as My
 * Work's sub-payment alert (get_my_work_base_v3, item #680): a finished project,
 * not closed (Completed is Jose's "paid", Decision 1), not the Trello backfill
 * (Decision 3), no whole-project payment, and a service line that pays out with
 * no payment record of its own.
 */
export function subPayChip(row: ListRowForSubPay, canSeePayments: boolean): string | null {
  if (!row.completion_date || row.stage === 'Completed' || row.source === 'Trello_Backfill') return null;
  if ((row.partner_payments ?? []).length > 0) return null;
  // ⛔ An empty payment list that means "not allowed to look" must never read as
  // "not paid". Payment records are readable by finance, executive and superuser
  // only (the partner_payments policies). For anyone else the chip says only
  // what the lines alone can prove: a line with pay and no sub.
  if (!canSeePayments) {
    const noSubLines = (row.project_services ?? []).filter(
      (l) => l.line_role === 'service' && Number(l.sub_rate ?? 0) > 0 && !l.assigned_partner_id,
    ).length;
    return noSubLines > 0 ? `Sub payments: ${noSubLines} ${plural(noSubLines, 'line needs', 'lines need')} a sub` : null;
  }
  const payable = (row.project_services ?? []).filter(
    (l) => l.line_role === 'service' && Number(l.sub_rate ?? 0) > 0,
  );
  const unpaid = payable.filter((l) => (l.partner_payments ?? []).length === 0);
  if (unpaid.length === 0) return null;
  const noSub = unpaid.filter((l) => !l.assigned_partner_id).length;
  if (noSub > 0) return `Sub payments: ${noSub} ${plural(noSub, 'line needs', 'lines need')} a sub`;
  return `Sub payments: ${payable.length - unpaid.length} of ${payable.length} lines paid`;
}
